import React, { useState, useEffect, useRef } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useSpring } from "framer-motion";
import { useTheme } from "../Context/ThemeContext";
import { useLanguage } from "../Context/LanguageContext";
import { FiSun, FiMoon, FiMenu, FiX, FiDownload, FiChevronDown, FiChevronUp } from "react-icons/fi";
import cvEn from "../Assets/cv-en.pdf";
import cvDe from "../Assets/cv-de.pdf";
import reference from "../Assets/reference.pdf";
import "./navbar.css";

const navLinks = [
  { path: "/", label: "_hello" },
  { path: "/about", label: "_about-me" },
  { path: "/projects", label: "_projects" },
  { path: "/blog", label: "_blog" },
  { path: "/contact", label: "_contact" },
];

const downloadOptions = [
  {
    id: "english",
    label: "english-version",
    href: cvEn,
    filename: "Ibrahim_Adebiyi_CV_English.pdf",
  },
  {
    id: "german",
    label: "german-version",
    href: cvDe,
    filename: "Ibrahim_Adebiyi_CV_German.pdf",
  },
  {
    id: "reference",
    label: "work-reference",
    href: reference,
    filename: "Ibrahim_Adebiyi_Work_Reference.pdf",
  },
];

const Navbar = () => {
  const { darkMode, toggleTheme } = useTheme();
  const { language, setLanguage, t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [isDownloadOpen, setIsDownloadOpen] = useState(false);
  const [isMobileDownloadOpen, setIsMobileDownloadOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 120,
    damping: 30,
    mass: 0.2,
  });
  const dropdownRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setIsDownloadOpen(false);
    setIsMobileDownloadOpen(false);
  }, [location]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDownloadOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  return (
    <>
      <motion.nav
        className={`navbar${scrolled ? " navbar--scrolled" : ""}`}
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        <div className="navbar__inner">
          {/* Logo / Brand */}
          <NavLink to="/" className="navbar__brand">
            <span className="navbar__brand-text mono">
              <span style={{ color: "var(--accent-purple)" }}>ibrahim</span>
              <span style={{ color: "var(--accent-cyan)" }}>.</span>
              <span style={{ color: "var(--text-muted)" }}>dev</span>
            </span>
          </NavLink>

          {/* Desktop Links */}
          <div className="navbar__links">
            {navLinks.map(({ path, label }) => {
              const isActive =
                path === "/"
                  ? location.pathname === "/" || location.pathname === "/home"
                  : location.pathname.startsWith(path);
              return (
                <NavLink key={path} to={path} className="navbar__link-wrapper">
                  <span className={`navbar__link${isActive ? " navbar__link--active" : ""}`}>
                    {t(label)}
                    {isActive && (
                      <motion.div
                        className="navbar__link-underline"
                        layoutId="nav-underline"
                        transition={{ type: "spring", stiffness: 380, damping: 30 }}
                      />
                    )}
                  </span>
                </NavLink>
              );
            })}
          </div>

          {/* Right side */}
          <div className="navbar__actions">
            {/* Language Switcher */}
            <div className="navbar__lang-switcher mono">
              <motion.div
                className="navbar__lang-slider"
                animate={{ x: language === "en" ? 0 : "100%" }}
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
              />
              <button
                className={`navbar__lang-btn ${language === "en" ? "navbar__lang-btn--active" : ""}`}
                onClick={() => setLanguage("en")}
              >
                EN
              </button>
              <button
                className={`navbar__lang-btn ${language === "de" ? "navbar__lang-btn--active" : ""}`}
                onClick={() => setLanguage("de")}
              >
                DE
              </button>
            </div>

            <motion.button
              className="navbar__theme-btn"
              onClick={toggleTheme}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              title="Toggle theme"
            >
              <AnimatePresence mode="wait">
                {darkMode ? (
                  <motion.span
                    key="sun"
                    initial={{ rotate: -90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: 90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <FiSun size={18} />
                  </motion.span>
                ) : (
                  <motion.span
                    key="moon"
                    initial={{ rotate: 90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: -90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <FiMoon size={18} />
                  </motion.span>
                )}
              </AnimatePresence>
            </motion.button>

            <div className="navbar__download" ref={dropdownRef}>
              <motion.button
                type="button"
                className="navbar__resume-btn"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setIsDownloadOpen((prev) => !prev)}
                aria-expanded={isDownloadOpen}
                aria-label="Download documents"
              >
                <FiDownload size={14} style={{ marginRight: 6 }} />
                {t("download resume")}
                {isDownloadOpen ? (
                  <FiChevronUp size={14} style={{ marginLeft: 6 }} />
                ) : (
                  <FiChevronDown size={14} style={{ marginLeft: 6 }} />
                )}
              </motion.button>

              <AnimatePresence>
                {isDownloadOpen && (
                  <motion.div
                    className="navbar__download-menu"
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.18 }}
                  >
                    {downloadOptions.map((item) => (
                      <a
                        key={item.id}
                        href={item.href}
                        download={item.filename}
                        className="navbar__download-item mono"
                        onClick={() => setIsDownloadOpen(false)}
                      >
                        {t(item.label)}
                      </a>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Mobile hamburger */}
            <motion.button
              className="navbar__hamburger"
              onClick={() => setIsOpen(!isOpen)}
              whileTap={{ scale: 0.9 }}
            >
              {isOpen ? <FiX size={22} /> : <FiMenu size={22} />}
            </motion.button>
          </div>
        </div>

        {/* Scroll progress bar */}
        <motion.div className="navbar__progress" style={{ scaleX }} />
      </motion.nav>

      {/* Mobile menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="mobile-menu"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.25 }}
          >
            {navLinks.map(({ path, label }, i) => (
              <motion.div
                key={path}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
              >
                <NavLink
                  to={path}
                  className={({ isActive }) =>
                    `mobile-menu__link${isActive ? " mobile-menu__link--active" : ""}`
                  }
                >
                  {t(label)}
                </NavLink>
              </motion.div>
            ))}

            <div className="mobile-menu__lang mono">
              <span className="mobile-menu__lang-label">_language:</span>
              <div className="mobile-menu__lang-options">
                <button
                  className={`mobile-menu__lang-btn ${language === "en" ? "active" : ""}`}
                  onClick={() => setLanguage("en")}
                >
                  english
                </button>
                <button
                  className={`mobile-menu__lang-btn ${language === "de" ? "active" : ""}`}
                  onClick={() => setLanguage("de")}
                >
                  german
                </button>
              </div>
            </div>

            <button
              type="button"
              className="mobile-menu__download-toggle mono"
              onClick={() => setIsMobileDownloadOpen((prev) => !prev)}
              aria-expanded={isMobileDownloadOpen}
            >
              <span style={{ display: "inline-flex", alignItems: "center" }}>
                <FiDownload size={14} style={{ marginRight: 6 }} />
                {t("download resume")}
              </span>
              {isMobileDownloadOpen ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />}
            </button>

            <AnimatePresence>
              {isMobileDownloadOpen && (
                <motion.div
                  className="mobile-menu__download-list"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                >
                  {downloadOptions.map((item) => (
                    <a
                      key={item.id}
                      href={item.href}
                      download={item.filename}
                      className="mobile-menu__resume"
                    >
                      {t(item.label)}
                    </a>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
